import React from "react"
import { ImageFieldValue } from "../utils/pageGroupPayload"
import { UploadedImage } from "../utils/templatePageForm"
import { ActionIcon, Box, Group, Image, Text } from "@mantine/core"
import { requiredMark } from "./BaseModal"
import { Dropzone } from "@mantine/dropzone"
import { XIcon } from "@phosphor-icons/react"

interface Props {
  label: React.ReactNode
  /** ラベル下の補足説明 */
  description?: React.ReactNode
  required?: boolean
  /** フォームの値。新規アップロード時は File[], それ以外は保存済みURLまたは空文字 */
  value: ImageFieldValue
  /** 今回アップロードした画像。プレビュー表示に使う */
  uploaded: UploadedImage | null
  /** 画像が未設定の時に表示するテンプレート別デフォルト画像 */
  defaultUrl?: string
  onDrop: (files: File[]) => void
  onClear: () => void
  error?: React.ReactNode
  /** 保存時にエラー箇所へスクロールするための識別子。フォームのフィールド名を指す */
  dataPath?: string
}

const ImageDropzoneField: React.FC<Props> = ({
  label,
  description,
  required = false,
  value,
  uploaded,
  defaultUrl = '',
  onDrop,
  onClear,
  error,
  dataPath,
}) => {
  // 保存済み画像のURL。新規アップロード中（File[]）のときは空になる
  const savedUrl = typeof value === 'string' ? value : ''
  // アップロード直後 → 保存済み画像 → テンプレートのデフォルト画像 の順に表示する
  const src = uploaded ? uploaded.preview : savedUrl || defaultUrl
  // デフォルト画像しか出ていない状態では消すものがないのでクリアは出さない
  const clearable = Boolean(uploaded || savedUrl)

  return (
    <Box data-path={dataPath}>
      <Group justify="space-between" align="flex-end" gap={8} mb={8} wrap="nowrap">
        <Text size="sm" fw={500}>
          {label}
        </Text>
        {required && requiredMark}
      </Group>

      {description && (
        <Text size="xs" c="#00000099" mb={8}>
          {description}
        </Text>
      )}

      <Group align="center" gap={16} wrap="nowrap">
        <Dropzone
          onDrop={onDrop}
          p={16}
          mih={90}
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Group justify="center" style={{ pointerEvents: 'none' }}>
            <Dropzone.Idle>
              <Text size="sm" ta="center" c="#00000099">
                ファイルをドロップするかクリックしてファイルを選択するとアップロードされます。
              </Text>
            </Dropzone.Idle>
            <Dropzone.Accept>
              <Text size="sm" ta="center" c="#00000099">
                ファイルをこちらにドロップするとアップロードされます。
              </Text>
            </Dropzone.Accept>
          </Group>
        </Dropzone>

        {src && (
          <Box pos="relative" style={{ flexShrink: 0 }}>
            <Image
              src={src}
              h={90}
              w="auto"
              fit="contain"
              style={{
                border: '1px solid #0000001A',
                borderRadius: 8,
                padding: 8,
                backgroundColor: '#fff',
              }}
            />
            {clearable && (
              <ActionIcon
                variant="filled"
                radius="xl"
                size="sm"
                pos="absolute"
                top={-8}
                right={-8}
                onClick={onClear}
                aria-label="画像をクリア"
              >
                <XIcon size={12} weight="bold" />
              </ActionIcon>
            )}
          </Box>
        )}
      </Group>

      {error && (
        <Text size="xs" c="red" mt={4}>
          {error}
        </Text>
      )}
    </Box>
  )
}

export default ImageDropzoneField
